import { AuthRole } from '@/src/constants/shared';
import { TFunction } from 'i18next';
import { CreateLSERequest, LSEMessage, LSEResponse, UpdateLSERequest } from '../types/Lse';
import { fromPlainObject } from '../utils/mapper.utils';
import { ApiClient } from './ApiClient';
import { i18nKeys } from '@/src/utils/i18n.utils';

export class LseAPI {
  private static readonly LSE_RESOURCE = '/admin/lse';

  constructor(private apiClient: ApiClient, private t: TFunction) {
    // do nothing
  }

  public async getLSEMessage(): Promise<LSEMessage | undefined> {
    return (await this.apiClient.get({
      path: LseAPI.LSE_RESOURCE,
      silent: true,
      responseMapper: (res) => fromPlainObject(res.message, LSEMessage),
    })) as Promise<LSEMessage | undefined>;
  }

  public getGlobalNotifications(): Promise<LSEMessage[]> {
    return this.apiClient.get({
      path: LseAPI.LSE_RESOURCE,
      silent: true,
      params: {
        'notification-type': 'global',
      },
      responseMapper: (res) => {
        if (typeof res === 'object' && res.hasOwnProperty('messages') && Array.isArray(res?.messages)) {
          return res.messages.map((item: object) => fromPlainObject(item, LSEMessage));
        }
        const single = fromPlainObject(res.message, LSEMessage);
        return single ? [single] : [];
      },
    }) as Promise<LSEMessage[]>;
  }

  public async createLSE(message: LSEMessage, silent = false): Promise<LSEMessage> {
    const request: CreateLSERequest = {
      message,
    };
    return (await this.apiClient.post({
      path: LseAPI.LSE_RESOURCE,
      body: request,
      silent,
      responseMapper: (res) => fromPlainObject(res, LSEResponse),
    })) as Promise<LSEMessage>;
  }

  public createGlobalNotification(
    message: LSEMessage,
    notificationType: 'lse' | 'global-notification',
    audience: AuthRole[],
    silent = false
  ): Promise<LSEMessage> {
    return this.apiClient.post({
      path: LseAPI.LSE_RESOURCE,
      body: {
        message,
        notificationType,
        audience,
      },
      silent,
      failMessage: this.t(i18nKeys.settings.globalNotifications.errors.createError),
      responseMapper: (res) => fromPlainObject(res, LSEResponse),
    });
  }

  public async updateLSE(
    message: LSEMessage,
    id?: string,
    notificationType: 'lse' | 'global-notification' = 'lse',
    silent = false
  ): Promise<LSEMessage> {
    const request: UpdateLSERequest = {
      id,
      message,
      notificationType,
    };
    return this.apiClient.patch({
      path: LseAPI.LSE_RESOURCE,
      body: request,
      silent,
      failMessage:
        notificationType === 'global-notification'
          ? this.t(i18nKeys.settings.globalNotifications.errors.updateError)
          : this.t(i18nKeys.settings.lse.errors.updateError),
      responseMapper: (res) => fromPlainObject(res, LSEResponse),
    }) as Promise<LSEMessage>;
  }

  public async deactivateLSE(id?: string, silent = false): Promise<void> {
    return this.apiClient.delete({
      ...(!!id ? { params: { id } } : {}),
      path: LseAPI.LSE_RESOURCE,
      silent,
      failMessage: !id
        ? this.t(i18nKeys.settings.globalNotifications.errors.deactivateError)
        : this.t(i18nKeys.settings.lse.errors.deactivateError),
    });
  }
}
